// @flow
import is from 'mz-utils/is'

// Types
export type Config = {
  resource?: string,
  disablePush?: boolean,
  disablePull?: boolean,
  resourceWithoutDomainPrefix?: boolean,
  allowUnreviewed?: boolean,
  domainLoader?: (Message) => Promise<{ [key: string]: string }>,
}
export type ExtendedDomain = Config & {
  domain: string,
}
export type Message = {
  id: string,
  domain: string,
  defaultMessage: string,
  description: string,
  config?: ExtendedDomain,
  values?: Object,
}
export type MessagesObject<T> = {
  [key: $Keys<T>]: Message,
}
export type MessageWithDescription = {|
  defaultMessage: string,
  description: string,
|}
export type SourceMessages = {
  [key: string]: string | MessageWithDescription,
}

/**
 * Function that returns messages object for given domain.
 * @param  {string} domain        Scope of the messages, should be unique
 *                                across the whole app. Will be concatinated
 *                                to message names to create message unique ID
 * @param  {object} obj           Initial object with default messages in default
 *                                language. The value of the object could be a string
 *                                or an object. An object should have `defaultMessage`
 *                                and `description` field.
 * @return {MessagesObject}
 */
const createMessages = <T: SourceMessages>(
  domain: string | ExtendedDomain,
  obj: T
): MessagesObject<T> => {
  const config = typeof domain === 'string' ? {} : (domain: ExtendedDomain)
  const normDomain = typeof domain === 'string' ? domain : config.domain

  return Object.keys(obj).reduce((acc, k) => {
    const value = obj[k]
    const defaultMessage = is.string(value)
      ? value
      : (value: any).defaultMessage
    const description = is.string(value) ? '' : (value: any).description

    acc[k] = {
      id: `${normDomain}.${k}`,
      domain: normDomain,
      defaultMessage,
      description,
      config,
    }
    return acc
  }, {})
}

export default createMessages
